import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  LockReset,
  MarkEmailRead,
  ErrorOutline,
  Visibility,
  VisibilityOff,
  CheckCircle,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { verifyResetCode, completePasswordReset, applyEmailActionCode } from '@services/authService';
import PasswordStrengthIndicator from '@components/auth/PasswordStrengthIndicator';

const resetSchema = yup.object({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Must contain at least one number')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

// ── Reset Password Mode ──────────────────────────────────────────────────────

const ResetPasswordMode = ({ oobCode }) => {
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    resolver: yupResolver(resetSchema),
  });

  useEffect(() => {
    verifyResetCode(oobCode)
      .then((emailAddress) => { setEmail(emailAddress); setVerifying(false); })
      .catch((err) => { setVerifyError(err.message); setVerifying(false); });
  }, [oobCode]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      await completePasswordReset(oobCode, data.password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  if (verifyError) return (
    <Box sx={{ textAlign: 'center' }}>
      <ErrorOutline sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>Link Invalid or Expired</Typography>
      <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{verifyError}</Alert>
      <Button component={Link} to="/forgot-password" variant="contained">Request a New Reset Link</Button>
    </Box>
  );

  if (success) return (
    <Box sx={{ textAlign: 'center' }}>
      <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>Password Reset!</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your password has been updated. You can now sign in with your new password.
      </Typography>
      <Button variant="contained" onClick={() => navigate('/login')}>Go to Login</Button>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <LockReset sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h5" fontWeight="bold" gutterBottom>Set New Password</Typography>
        <Typography variant="body2" color="text.secondary">
          Setting a new password for <strong>{email}</strong>
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          fullWidth label="New Password"
          type={showPassword ? 'text' : 'password'}
          margin="normal"
          {...register('password')}
          error={!!errors.password}
          helperText={errors.password?.message}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <PasswordStrengthIndicator password={watch('password')} />

        <TextField
          fullWidth label="Confirm New Password"
          type={showConfirm ? 'text' : 'password'}
          margin="normal"
          {...register('confirmPassword')}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit" fullWidth variant="contained" size="large"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LockReset />}
          sx={{ mt: 3 }}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </Box>
    </Box>
  );
};

// ── Verify Email Mode ────────────────────────────────────────────────────────

const VerifyEmailMode = ({ oobCode }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    applyEmailActionCode(oobCode)
      .then(() => setStatus('success'))
      .catch((err) => { setErrorMsg(err.message); setStatus('error'); });
  }, [oobCode]);

  if (status === 'verifying') return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  if (status === 'error') return (
    <Box sx={{ textAlign: 'center' }}>
      <ErrorOutline sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>Verification Failed</Typography>
      <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{errorMsg}</Alert>
      <Button component={Link} to="/login" variant="contained">Go to Login</Button>
    </Box>
  );

  return (
    <Box sx={{ textAlign: 'center' }}>
      <MarkEmailRead sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>Email Verified!</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your email address has been verified. You're all set.
      </Typography>
      <Button variant="contained" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
    </Box>
  );
};

// ── Unknown / Missing Mode ───────────────────────────────────────────────────

const UnknownMode = () => (
  <Box sx={{ textAlign: 'center' }}>
    <ErrorOutline sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
    <Typography variant="h5" fontWeight="bold" gutterBottom>Invalid Link</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      This link is not recognized. Please use the link directly from your email.
    </Typography>
    <Button component={Link} to="/login" variant="contained">Go to Login</Button>
  </Box>
);

// ── Main Page ────────────────────────────────────────────────────────────────

const AuthActionPage = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const renderMode = () => {
    if (!oobCode) return <UnknownMode />;
    switch (mode) {
      case 'resetPassword': return <ResetPasswordMode oobCode={oobCode} />;
      case 'verifyEmail':   return <VerifyEmailMode oobCode={oobCode} />;
      default:              return <UnknownMode />;
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {renderMode()}
      </Paper>
    </Container>
  );
};

export default AuthActionPage;
